/**
 * Session mirror — copia de la sesión NextAuth en localStorage.
 *
 * Propósito: que mientras el dispositivo esté offline (o entre cargas) podamos
 * saber QUIÉN es el usuario activo para atribuir las acciones encoladas
 * (createdBy/updatedBy) sin depender de que la cookie/JWT esté disponible
 * o sin tener que pedir al servidor.
 *
 * No reemplaza a NextAuth — es un espejo de lectura que se actualiza al boot
 * y al cambiar de sesión. Si NextAuth dice algo distinto, NextAuth gana
 * (la mirror se sobreescribe).
 */

export type MirroredUser = {
  userId: string;
  name: string | null;
  email: string | null;
  role: string;
  personalId?: number | null;
  telefono?: string | null;
  image?: string | null;
  /** ms desde epoch — última vez que el mirror se actualizó */
  lastSyncedAt: number;
};

const STORAGE_KEY = "cacique_session_mirror";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getSessionMirror(): MirroredUser | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MirroredUser;
    if (!parsed || typeof parsed.userId !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setSessionMirror(user: Omit<MirroredUser, "lastSyncedAt">): void {
  if (!isBrowser()) return;
  try {
    const payload: MirroredUser = { ...user, lastSyncedAt: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

export function clearSessionMirror(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Comparador para evitar escrituras innecesarias.
 */
export function mirrorEquals(
  a: MirroredUser | null,
  b: Omit<MirroredUser, "lastSyncedAt"> | null
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.userId === b.userId &&
    a.email === b.email &&
    a.name === b.name &&
    a.role === b.role &&
    (a.personalId ?? null) === (b.personalId ?? null)
  );
}
